import { useEffect, useState } from 'react';
import { MessageCircle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsOf = (name = '') =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '·';

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const CommentsSection = ({ postSlug, postType = 'article' }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ author_name: '', author_email: '', content: '' });

  useEffect(() => {
    let cancelled = false;
    const fetchComments = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/blog/${postSlug}/comments`);
        if (!cancelled) {
          setItems(res.data.items || []);
          setTotal(res.data.total || 0);
        }
      } catch (err) {
        console.error('Comments fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchComments();
    return () => {
      cancelled = true;
    };
  }, [postSlug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.author_name.trim().length < 2 || form.content.trim().length < 5) {
      toast.error('Please enter your name and a complete comment.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/blog/${postSlug}/comments`, form);
      setItems((prev) => [res.data, ...prev]);
      setTotal((t) => t + 1);
      setForm({ author_name: '', author_email: '', content: '' });
      toast.success('Comment posted — thank you for joining the conversation.');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error('You just posted that — please wait a moment.');
      } else if (status === 422) {
        toast.error('Please check your name, email, and comment.');
      } else {
        toast.error('Could not post your comment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16 pt-16 border-t border-[#E5E0D8]" data-testid="comments-section">
      <div className="flex items-baseline justify-between mb-10 pb-6 border-b border-[#E5E0D8]">
        <div>
          <p className="overline overline-gold mb-2">Discussion</p>
          <h2 className="font-heading text-3xl lg:text-4xl text-[#111]">
            Reader <span className="italic">Comments</span>
          </h2>
        </div>
        <p className="overline flex items-center gap-2">
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span data-testid="comments-count">{total}</span>
          {' '}
          {total === 1 ? 'reply' : 'replies'}
        </p>
      </div>

      {/* Comment Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-[#E5E0D8] p-8 lg:p-10 mb-12"
        data-testid="comments-form"
      >
        <p className="overline overline-gold mb-6">Join The Conversation</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-6">
          <div>
            <label htmlFor="comment-name" className="overline block mb-3">
              Your Name
            </label>
            <input
              id="comment-name"
              name="author_name"
              type="text"
              required
              minLength={2}
              maxLength={50}
              value={form.author_name}
              onChange={handleChange}
              placeholder="Isabella Chen"
              className="input-underline w-full text-[#111] placeholder:text-[#999]"
              data-testid="comment-name-input"
            />
          </div>
          <div>
            <label htmlFor="comment-email" className="overline block mb-3">
              Email <span className="text-[#888] normal-case tracking-normal">(not published)</span>
            </label>
            <input
              id="comment-email"
              name="author_email"
              type="email"
              required
              value={form.author_email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="input-underline w-full text-[#111] placeholder:text-[#999]"
              data-testid="comment-email-input"
            />
          </div>
        </div>

        <div className="mb-8">
          <label htmlFor="comment-body" className="overline block mb-3">
            Your Comment
          </label>
          <textarea
            id="comment-body"
            name="content"
            required
            rows={5}
            minLength={5}
            maxLength={2000}
            value={form.content}
            onChange={handleChange}
            placeholder={`Share your thoughts on this ${postType}…`}
            className="input-underline w-full text-[#111] placeholder:text-[#999] resize-none"
            data-testid="comment-content-input"
          />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4 pt-4 border-t border-[#E5E0D8]">
          <p className="text-xs text-[#888] max-w-md">
            We moderate spam but never edit reader voices. Your email is used only for replies and
            is never published or sold.
          </p>
          <button
            type="submit"
            disabled={submitting}
            className="btn-editorial-inverse btn-editorial inline-flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="comment-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                Posting…
              </>
            ) : (
              <>
                Post Comment
                <Send className="w-3 h-3" strokeWidth={1.5} />
              </>
            )}
          </button>
        </div>
      </form>

      {/* Comment List */}
      <div data-testid="comments-list">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-xs tracking-[0.15em] uppercase text-[#888]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={1.5} />
            Loading discussion…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#E5E0D8]">
            <MessageCircle className="w-8 h-8 text-[#C69C6D] mx-auto mb-4" strokeWidth={1.2} />
            <p className="overline overline-gold mb-2">Be The First</p>
            <p className="text-[#555] max-w-sm mx-auto">
              No comments yet. Start the conversation — your voice opens the floor.
            </p>
          </div>
        ) : (
          <ul className="space-y-px bg-[#E5E0D8]">
            {items.map((c) => (
              <li
                key={c.id}
                className="bg-[#FAF9F6] flex gap-5 p-6 lg:p-8"
                data-testid={`comment-${c.id}`}
              >
                <div
                  aria-hidden="true"
                  className="flex-shrink-0 w-12 h-12 border border-[#C69C6D] text-[#C69C6D] font-heading text-lg flex items-center justify-center"
                >
                  {initialsOf(c.author_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-4 mb-2">
                    <p className="font-heading text-lg text-[#111]">{c.author_name}</p>
                    <span className="text-xs tracking-[0.1em] uppercase text-[#888] flex-shrink-0">
                      {timeAgo(c.created_at)}
                    </span>
                  </div>
                  <p className="text-[#555] leading-relaxed whitespace-pre-line break-words">
                    {c.content}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default CommentsSection;
